import React from 'react';
import PropTypes from 'prop-types';
import InputLabel from '../InputLabel';
import Feedback from '../Feedback';
import StackView from '../StackView';
import { useThemeTokens } from '../ThemeProvider';
import useInputSupports from './useInputSupports';
import { getTokensPropType, useCopy } from '../utils';
import dictionary from './dictionary';
import tooltipPropTypes from '../Tooltip/shared';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const InputSupports = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    copy = 'en',
    label,
    hint,
    hintPosition = 'inline',
    feedback,
    feedbackTokens = {},
    feedbackProps = {},
    tooltip,
    validation,
    maxCharacterAllowed,
    inputValue,
    nativeID
  } = _ref;
  const {
    space
  } = useThemeTokens('InputSupports');
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const maxCharsReachedErrorMessage = inputValue?.length > maxCharacterAllowed ? getCopy('maxCharsMessage').replace(/%\{charCount\}/g, maxCharacterAllowed) : '';
  const feedbackValidation = inputValue?.length > maxCharacterAllowed ? 'error' : validation;
  const {
    inputId,
    hintId,
    feedbackId,
    a11yProps
  } = useInputSupports({
    feedback,
    hint,
    label,
    validation,
    nativeID,
    copy,
    maxCharacterAllowed,
    charactersCount: maxCharacterAllowed - inputValue?.length
  });
  return /*#__PURE__*/_jsxs(StackView, {
    space: space,
    ref: ref,
    children: [label && /*#__PURE__*/_jsx(InputLabel, {
      copy: copy,
      label: label,
      hint: hint,
      hintPosition: hintPosition,
      hintId: hintId,
      tooltip: tooltip,
      forId: inputId,
      characterCount: inputValue?.length,
      maxCharacterAllowed: maxCharacterAllowed
    }), typeof children === 'function' ? children({
      inputId,
      ...a11yProps,
      validation: feedbackValidation
    }) : children, feedback || maxCharsReachedErrorMessage ? /*#__PURE__*/_jsx(Feedback, {
      id: feedbackId,
      title: feedback || maxCharsReachedErrorMessage,
      validation: feedbackValidation,
      tokens: feedbackTokens,
      variant: {
        icon: feedbackProps.showFeedbackIcon
      },
      ...feedbackProps
    }) : null]
  });
});
InputSupports.displayName = 'InputSupports';
InputSupports.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: PropTypes.oneOf(['en', 'fr']),
  /**
   * The input label.
   */
  label: PropTypes.string,
  /**
   * A short description of the expected input.
   */
  hint: PropTypes.string,
  /**
   * Position of the hint relative to label.
   */
  hintPosition: PropTypes.oneOf(['inline', 'below']),
  /**
   * A detailed description of validation error/success or additional instructions.
   * Visual variant is determined based on the `validation` prop.
   */
  feedback: PropTypes.string,
  /**
   * Additional tokens to override the default feedback tokens.
   */
  feedbackTokens: getTokensPropType('Feedback'),
  /**
   * Additional props to pass to the `Feedback` component.
   */
  feedbackProps: PropTypes.object,
  /**
   * an optional `Tooltip`. If set, tooltip will be shown next to the label.
   * Props can be passed to the tooltip in one of the following ways.
   * 1. `tooltip` as a string - The content of the tooltip.
   * 2. `tooltip` as an object - Tooltip component props to be passed.
   */
  tooltip: PropTypes.oneOfType([PropTypes.shape(tooltipPropTypes), PropTypes.string]),
  /**
   * Use to visually mark an input as valid or invalid.
   */
  validation: PropTypes.oneOf(['error', 'success']),
  /**
   * ID for DOM element on web
   */
  nativeID: PropTypes.string,
  /**
   * The text value of a TextArea or TextInput
   */
  inputValue: PropTypes.string,
  /**
   * Max number of characters that allows an input text.
   */
  maxCharacterAllowed: PropTypes.number
};
export default InputSupports;